import type { DocumentAuditTrailModel } from "@/api/cle-api/models/documents";
import { DocumentAuditTrailAction } from "@/api/cle-api/models/documents";

import {
  AUDIT_STATUS_CHANGE_REASON_LABELS_DA,
  formatAuditAiDecisionLabel,
  formatAuditStatusLabel,
} from "./auditTrailLabels";
import { getAuditTrailFieldChanges } from "./formatAuditTrailFieldChange";

const GENERIC_FIELD_EDIT_COMMENT = "fields edited";

const formatAssignmentSummary = (
  assignment: NonNullable<DocumentAuditTrailModel["details"]>["assignment"],
): string | null => {
  if (!assignment) {
    return null;
  }

  const assignee = assignment.assigneeDisplayName?.trim();
  const previous = assignment.previousAssigneeDisplayName?.trim();

  if (previous && assignee) {
    return `Tildelt fra ${previous} til ${assignee}`;
  }

  if (assignee) {
    return `Tildelt til ${assignee}`;
  }

  return null;
};

const formatStatusChangeSummary = (
  statusChange: NonNullable<DocumentAuditTrailModel["details"]>["statusChange"],
): string | null => {
  if (!statusChange) {
    return null;
  }

  if (
    statusChange.reason &&
    statusChange.reason in AUDIT_STATUS_CHANGE_REASON_LABELS_DA
  ) {
    return AUDIT_STATUS_CHANGE_REASON_LABELS_DA[statusChange.reason];
  }

  const fromLabel = formatAuditStatusLabel(statusChange.fromStatus);
  const toLabel = formatAuditStatusLabel(statusChange.toStatus);

  if (fromLabel && toLabel) {
    return `Status ændret fra ${fromLabel} til ${toLabel}`;
  }

  return null;
};

const formatAiAnalysisSummary = (
  aiAnalysis: NonNullable<DocumentAuditTrailModel["details"]>["aiAnalysis"],
): string | null => {
  if (!aiAnalysis) {
    return null;
  }

  const decisionLabel =
    formatAuditAiDecisionLabel(aiAnalysis.decision) ?? aiAnalysis.decision;
  const base = `AI-vurdering: ${decisionLabel}`;

  if (aiAnalysis.rejectionReason?.trim()) {
    return `${base}. ${aiAnalysis.rejectionReason.trim()}`;
  }

  return base;
};

const formatLegacyCommentSummary = (comment?: string): string | null => {
  if (!comment?.trim()) {
    return null;
  }

  const trimmed = comment.trim();

  if (trimmed.toLowerCase() === GENERIC_FIELD_EDIT_COMMENT) {
    return null;
  }

  const assignedMatch = /^Assigned to (.+)$/i.exec(trimmed);
  if (assignedMatch) {
    return `Tildelt til ${assignedMatch[1]}`;
  }

  const reassignedMatch = /^Reassigned from (.+) to (.+)$/i.exec(trimmed);
  if (reassignedMatch) {
    return `Tildelt fra ${reassignedMatch[1]} til ${reassignedMatch[2]}`;
  }

  const unassignedMatch = /^Unassigned by (.+)$/i.exec(trimmed);
  if (unassignedMatch) {
    return `Tildeling fjernet af ${unassignedMatch[1]}`;
  }

  if (trimmed === "Human review requested after comment") {
    return AUDIT_STATUS_CHANGE_REASON_LABELS_DA.human_review_requested;
  }

  if (trimmed === "Review thread reopened after new lawyer comment") {
    return AUDIT_STATUS_CHANGE_REASON_LABELS_DA.thread_reopened_after_comment;
  }

  const aiMatch =
    /^AI analysis: (\w+) \(status ([^ ]+) -> ([^)]+)\)(?:\. Reason: (.+))?$/i.exec(
      trimmed,
    );
  if (aiMatch) {
    const decisionLabel = formatAuditAiDecisionLabel(aiMatch[1]) ?? aiMatch[1];
    const base = `AI-vurdering: ${decisionLabel}`;
    return aiMatch[4] ? `${base}. ${aiMatch[4]}` : base;
  }

  return trimmed;
};

const formatReportingSummary = (
  reporting: NonNullable<DocumentAuditTrailModel["details"]>["reporting"],
): string | null => {
  if (!reporting) {
    return null;
  }

  const targetLabels: Record<string, string> = {
    period: "efteruddannelsesperiode",
    injunction: "påbud",
    imposition: "pålæg",
  };
  const targetLabel =
    targetLabels[reporting.targetType] ?? reporting.targetType;

  return `Anvendt i indberetning for ${targetLabel}`;
};

const formatAttachmentSummary = (
  attachment: NonNullable<DocumentAuditTrailModel["details"]>["attachment"],
  action: DocumentAuditTrailModel["action"],
): string | null => {
  if (!attachment) {
    return null;
  }

  const fileName = attachment.fileName?.trim();

  if (action === DocumentAuditTrailAction.DELETED) {
    return fileName
      ? `Supplerende dokumentation fjernet: ${fileName}`
      : "Supplerende dokumentation fjernet";
  }

  return fileName
    ? `Supplerende dokumentation uploadet: ${fileName}`
    : "Supplerende dokumentation uploadet";
};

export const formatAuditTrailSummary = (
  item: DocumentAuditTrailModel,
): string | null => {
  const details = item.details;
  const fieldChanges = getAuditTrailFieldChanges(details?.fieldChanges);

  if (details?.attachment) {
    return formatAttachmentSummary(details.attachment, item.action);
  }

  if (details?.assignment) {
    return formatAssignmentSummary(details.assignment);
  }

  if (details?.statusChange?.reason) {
    return formatStatusChangeSummary(details.statusChange);
  }

  if (details?.aiAnalysis) {
    return formatAiAnalysisSummary(details.aiAnalysis);
  }

  if (details?.reporting) {
    return formatReportingSummary(details.reporting);
  }

  if (fieldChanges.length > 0) {
    return null;
  }

  if (details?.statusChange) {
    return formatStatusChangeSummary(details.statusChange);
  }

  if (item.action === DocumentAuditTrailAction.UPLOADED) {
    return "Dokument uploadet";
  }

  return formatLegacyCommentSummary(item.comment);
};
